/* ============================================================
   viewPipeline — 필터·정렬·검색 공통 파이프라인 + 뷰 공용 유틸
   - 모든 뷰 타입(표/보드/캘린더/갤러리/리스트/타임라인/차트)이
     DatabasePage 의 단일 useMemo 파이프라인(applyPipeline)을 공유한다.
   - 필터/정렬 상태는 db_views.config (jsonb) 에 persist 된다.
     · config.filters: [{ id, column, op, value }]  (다중 필터 AND)
     · config.sorts:   [{ column, dir: 'asc'|'desc' }] (배열 순서 = 우선순위)
   ============================================================ */

/* ---------------- 공용 표시 유틸 (신규 뷰 컴포넌트에서 import) ---------------- */
export const COLOR_CLASS = {
  gray: 'tg', default: 'tg',
  blue: 'tbl', green: 'tgr', yellow: 'tyl',
  orange: 'tor', purple: 'tpu', red: 'trd', pink: 'trd', brown: 'tor',
};

export function optClass(col, value) {
  const opt = (col?.options || []).find((o) => o.name === value);
  return COLOR_CLASS[opt?.color] || 'tg';
}

const AVA_COLORS = ['#2383e2', '#9b59b6', '#27ae60', '#e67e22', '#16a085', '#c0392b'];
export function avaColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) % AVA_COLORS.length;
  return AVA_COLORS[h];
}

export function fmtShort(v) {
  if (!v) return '';
  const s = String(v).slice(0, 10);
  const parts = s.split('-');
  if (parts.length === 3) return `${Number(parts[1])}/${Number(parts[2])}`;
  return s;
}

/* ---------------- 필터 연산자 정의 ---------------- */
export const OPS_BY_TYPE = {
  title:    [{ op: 'contains', label: '포함' }],
  text:     [{ op: 'contains', label: '포함' }],
  url:      [{ op: 'contains', label: '포함' }],
  person:   [{ op: 'contains', label: '포함' }],
  relation: [{ op: 'contains', label: '포함' }],
  select:   [{ op: 'eq', label: '=' }],
  checkbox: [{ op: 'checked', label: '체크됨' }, { op: 'unchecked', label: '체크 안 됨' }],
  number:   [{ op: 'eq', label: '=' }, { op: 'gt', label: '>' }, { op: 'lt', label: '<' }],
  date:     [{ op: 'before', label: '이전' }, { op: 'after', label: '이후' }, { op: 'today', label: '오늘' }],
  // formula/rollup 은 렌더 시 계산값 기준 — 텍스트 포함 + 숫자 비교 둘 다 지원
  formula:  [{ op: 'contains', label: '포함' }, { op: 'eq', label: '=' }, { op: 'gt', label: '>' }, { op: 'lt', label: '<' }],
  rollup:   [{ op: 'contains', label: '포함' }, { op: 'eq', label: '=' }, { op: 'gt', label: '>' }, { op: 'lt', label: '<' }],
};

export function opsFor(col) {
  return OPS_BY_TYPE[col?.type] || OPS_BY_TYPE.text;
}

/* 값 입력이 필요 없는 연산자 */
export function needsValue(op) {
  return !['checked', 'unchecked', 'today'].includes(op);
}

/* 조건이 완성돼 실제로 적용 가능한지 (미완성 조건은 무시 = 전체 통과) */
export function isFilterComplete(f) {
  if (!f || !f.column || !f.op) return false;
  if (!needsValue(f.op)) return true;
  return f.value !== '' && f.value != null;
}

/* ---------------- 값 접근/문자열화 ---------------- */
export function rowValue(row, col) {
  if (!col) return '';
  if (col.key === 'title' || col.type === 'title') return row.title;
  return row.properties?.[col.key];
}

/* relation 배열([{id,title}]) 등도 검색 가능한 평문으로 */
export function textOf(v) {
  if (v == null) return '';
  if (Array.isArray(v)) {
    return v
      .map((it) => (it && typeof it === 'object' ? (it.title ?? it.name ?? it.id ?? '') : String(it)))
      .join(' ');
  }
  return String(v);
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isEmpty(v) {
  return v == null || v === '' || (Array.isArray(v) && v.length === 0);
}

/* 계산형(formula/rollup) 값의 숫자 여부 판정 */
function bothNumeric(a, b) {
  return (
    a !== '' && a != null && b !== '' && b != null &&
    !Number.isNaN(Number(a)) && !Number.isNaN(Number(b))
  );
}

/* ---------------- 필터 매칭 ----------------
   v 는 호출부에서 getValue(row, col) 로 계산해 전달한다
   (formula/rollup 계산값, person 이름, relation 제목 기준). */
function matchFilter(v, col, f) {
  switch (f.op) {
    case 'contains':
      return textOf(v).toLowerCase().includes(String(f.value).toLowerCase());
    case 'eq':
      if (col.type === 'number') return !isEmpty(v) && Number(v) === Number(f.value);
      if ((col.type === 'formula' || col.type === 'rollup') && bothNumeric(v, f.value)) {
        return Number(v) === Number(f.value);
      }
      return String(v ?? '') === String(f.value);
    case 'gt':
      return !isEmpty(v) && Number(v) > Number(f.value);
    case 'lt':
      return !isEmpty(v) && Number(v) < Number(f.value);
    case 'checked':
      return v === true || v === 'true';
    case 'unchecked':
      return !(v === true || v === 'true');
    case 'before':
      return !isEmpty(v) && String(v).slice(0, 10) < String(f.value);
    case 'after':
      return !isEmpty(v) && String(v).slice(0, 10) > String(f.value);
    case 'today':
      return !isEmpty(v) && String(v).slice(0, 10) === todayIso();
    default:
      return true;
  }
}

/* ---------------- 정렬 비교 ---------------- */
function compareValues(a, b, type) {
  if (type === 'number') return Number(a) - Number(b);
  if (type === 'formula' || type === 'rollup') {
    // 계산값이 둘 다 숫자면 숫자 비교, 아니면 문자열 비교
    if (bothNumeric(a, b)) return Number(a) - Number(b);
    return textOf(a).localeCompare(textOf(b), 'ko');
  }
  if (type === 'checkbox') {
    const na = a === true || a === 'true' ? 1 : 0;
    const nb = b === true || b === 'true' ? 1 : 0;
    return na - nb;
  }
  // date 는 ISO(YYYY-MM-DD) 문자열이라 문자열 비교로 충분
  return textOf(a).localeCompare(textOf(b), 'ko');
}

/* ============================================================
   applyPipeline — 검색 → 필터(AND) → 다중 정렬(우선순위 순)
   rows 원본은 변형하지 않고 새 배열을 반환한다.
   ============================================================ */
export function applyPipeline(rows, columns, { filters = [], sorts = [], search = '', getValue } = {}) {
  let out = rows;
  // getValue(row, col): 값 접근자. 미지정 시 원본값(rowValue).
  // DatabasePage 는 computeValue.makeGetValue 를 주입해 formula/rollup 계산값,
  // person 이름, relation 제목 기준으로 검색/필터/정렬한다.
  const valOf = typeof getValue === 'function' ? getValue : rowValue;

  // 1) 검색 — 모든 텍스트성 속성 부분일치 (대소문자 무시)
  const q = String(search || '').trim().toLowerCase();
  if (q) {
    out = out.filter((row) => {
      if (textOf(row.title).toLowerCase().includes(q)) return true;
      return columns.some(
        (col) => col.type !== 'title' && textOf(valOf(row, col)).toLowerCase().includes(q)
      );
    });
  }

  // 2) 필터 — 완성된 조건만 AND 적용 (컬럼이 삭제된 조건은 무시)
  const active = (filters || []).filter(isFilterComplete);
  if (active.length) {
    out = out.filter((row) =>
      active.every((f) => {
        const col = columns.find((c) => c.key === f.column);
        if (!col) return true;
        return matchFilter(valOf(row, col), col, f);
      })
    );
  }

  // 3) 정렬 — 배열 순서가 우선순위. 빈 값은 방향과 무관하게 항상 뒤로.
  const activeSorts = (sorts || []).filter((s) => s && s.column);
  if (activeSorts.length) {
    out = [...out].sort((a, b) => {
      for (const s of activeSorts) {
        const col = columns.find((c) => c.key === s.column);
        if (!col) continue;
        const va = valOf(a, col);
        const vb = valOf(b, col);
        const ea = isEmpty(va);
        const eb = isEmpty(vb);
        if (ea || eb) {
          if (ea && eb) continue;
          return ea ? 1 : -1;
        }
        const cmp = compareValues(va, vb, col.type);
        if (cmp !== 0) return s.dir === 'desc' ? -cmp : cmp;
      }
      return 0;
    });
  } else if (q || active.length) {
    out = [...out];
  }

  return out;
}
